import * as React from 'react';
import { useState } from 'react';
import { CenterFormWidget } from '../../widgets/layout/CenterFormWidget';
import { ButtonWidget } from '../../widgets/buttons/ButtonWidget';
import { FormLayoutWidget } from '../../widgets/layout/FormLayoutWidget';
import { FormikPhoneInput, InputWrapperWidget } from '../../widgets/inputs';
import { styled } from '../../theme';
import SvgInfoIcon from '../../widgets/svgs/SvgInfoIcon';
import { setupTooltipProps, TooltipLength, TooltipPosition } from '../../utils/Tooltips';
import { Keys, useKeyPress } from '../../hooks/useKeyPress';
import { useFormik } from 'formik';
import { ParagraphWidget } from '../../widgets/forms/ParagraphWidget';
import { SignupPayload } from './SignupPage';
import { useSystem } from '../../hooks/useSystem';
import { useFlightLogger } from '../../hooks/useFlightLogger';
import { SignupActionSource } from '@journeyapps-platform/types-flight-log';
import { DeviceMaxWidth } from '../../utils/device';
import { GetSignupUserResponse } from '@journeyapps-platform/types-accounts-hub';

export interface SignupStep2WidgetProps {
  next: (number: string) => any;
  user: GetSignupUserResponse;
  payload: Partial<SignupPayload>;
}

type SignupStep2Values = {
  mobileNumber: string;
};

export const SignupStep2Widget: React.FC<SignupStep2WidgetProps> = (props) => {
  const [failed, setFailed] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const system = useSystem();
  const flightLogger = useFlightLogger({ stepId: 'signup-step2', userId: props.user.id });
  const submit = async (value: string) => {
    if (!value) {
      return;
    }
    setFailed(false);
    setSubmitting(true);
    try {
      await system.sessionAccountsPortalClient.sendOTP({
        phone_number: value,
        token: system.tokenStore.token || ''
      });
      props.next(value);
    } catch (ex) {
      console.error('Failed to send code', ex);
      flightLogger.logAction({ source: SignupActionSource.SYSTEM, action_id: 'otp-failed' });
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };
  const formik = useFormik({
    initialValues: {
      mobileNumber: ''
    },
    validate: (values) => {
      const errors: Partial<SignupStep2Values> = {};
      if (!values.mobileNumber) {
        errors.mobileNumber = 'This is a required field';
      }
      return errors;
    },
    onSubmit: (values) => {
      submit(values.mobileNumber);
    }
  });

  useKeyPress(Keys.ENTER, {
    onKeyPressed: () => {
      flightLogger.logAction({ source: SignupActionSource.KEYPRESS, action_id: 'submit-via-enter' });
      formik.submitForm();
    }
  });

  return (
    <CenterFormWidget primaryLabel="Secure your account using 2FA" secondaryLabel="Step 1 of 2">
      <FormLayoutWidget
        sections={[
          {
            key: 'mobile',
            elements: [
              <ParagraphWidget>{`Please provide your phone number\nto receive your security code.`}</ParagraphWidget>,
              <S.MobileNumber>
                <InputWrapperWidget
                  error={(failed && 'Please enter a valid mobile number') || formik.errors.mobileNumber}
                >
                  <FormikPhoneInput
                    key="mobile"
                    placeholder="Mobile number"
                    name={'mobileNumber'}
                    value={formik.values.mobileNumber}
                    formik={formik}
                    onChange={(value) => {
                      setFailed(false);
                      formik.setFieldValue('mobileNumber', value);
                    }}
                    onBlur={(e) => {
                      flightLogger.logAction({
                        source: SignupActionSource.INPUT,
                        action_id: 'otp-number-entered'
                      });
                    }}
                    hasError={failed || !!formik.errors.mobileNumber}
                  />
                </InputWrapperWidget>
                <S.IconBox
                  {...setupTooltipProps(
                    {
                      tooltip: `We will only use your phone number to secure your account, and never for anything else. We will never share your number with any third parties.`,
                      tooltipPos: TooltipPosition.BOTTOM_RIGHT
                    },
                    { length: TooltipLength.LARGE }
                  )}
                >
                  <SvgInfoIcon />
                </S.IconBox>
              </S.MobileNumber>
            ]
          },
          {
            key: 'submit',
            elements: [
              <ButtonWidget
                label="Next"
                loadingOverride={submitting}
                action={async (event, loading) => {
                  flightLogger.logAction({ source: SignupActionSource.CLICK, action_id: 'send-otp-button' });
                  loading(true);
                  await submit(formik.values.mobileNumber);
                  loading(false);
                }}
              />
            ]
          }
        ]}
      />
    </CenterFormWidget>
  );
};

const BASE_LABEL = 'signup-otp-widget';
namespace S {
  export const MobileNumber = styled.div`
    label: ${BASE_LABEL}__number-box;
    display: flex;
    flex-direction: row;
    min-width: 300px;

    @media (${DeviceMaxWidth.mobileS}) {
      min-width: 280px;
    }
  `;

  export const IconBox = styled.div`
    label: ${BASE_LABEL}__icon;
    margin-left: 16px;
    margin-top: 8px;
    --balloon-color: ${(p) => p.theme.forms.tooltip.background};
  `;
}
